/**
 * SIDetailPanel Component
 * Full detail view for a single Supplemental Instruction including
 * status controls, distribution, and PDF download.
 *
 * Follows RFIDetailPanel pattern.
 */
import { useState } from 'react';
import {
  SI,
  SI_STATUS_CONFIG,
  VALID_SI_TRANSITIONS,
} from '@/types/supplemental_instructions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Download, Send } from 'lucide-react';
import { useSI, useUpdateSIStatus, useDistributeSI } from '@/hooks';
import { api } from '@/lib/api';

interface SIDetailPanelProps {
  siId: string | null;
  onClose: () => void;
  onEdit: (si: SI) => void;
}

function formatDate(val: string | null | undefined) {
  if (!val) return null;
  return new Date(val).toLocaleDateString();
}

export function SIDetailPanel({ siId, onClose, onEdit }: SIDetailPanelProps) {
  const { data, isLoading } = useSI(siId);
  const updateStatus = useUpdateSIStatus();
  const distributeSI = useDistributeSI();

  const [showDistributeForm, setShowDistributeForm] = useState(false);
  const [distributeEmails, setDistributeEmails] = useState('');
  const [distributeNote, setDistributeNote] = useState('');

  const si = data?.si;

  const handleClose = () => {
    setShowDistributeForm(false);
    setDistributeEmails('');
    setDistributeNote('');
    onClose();
  };

  const handleStatusChange = async (newStatus: 'issued' | 'superseded') => {
    if (!si) return;
    await updateStatus.mutateAsync({ siId: si.id, status: newStatus });
  };

  const handleDistribute = async () => {
    if (!si || !distributeEmails.trim()) return;
    const emails = distributeEmails
      .split(/[,\n]/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length === 0) return;

    await distributeSI.mutateAsync({
      siId: si.id,
      data: {
        recipient_emails: emails,
        custom_note: distributeNote || null,
      },
    });
    setDistributeEmails('');
    setDistributeNote('');
    setShowDistributeForm(false);
  };

  const handleDownloadPDF = async () => {
    if (!si) return;
    const fileName = `${si.si_number} - ${si.title}.pdf`;
    await api.sis.downloadPDF(si.id, fileName);
  };

  const allowedTransitions = si ? VALID_SI_TRANSITIONS[si.status] : [];

  return (
    <Dialog open={!!siId} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {isLoading || !si ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="text-sm font-mono text-muted-foreground">{si.si_number}</span>
                  <DialogTitle className="mt-1">{si.title}</DialogTitle>
                </div>
                <Badge className={SI_STATUS_CONFIG[si.status].className}>
                  {SI_STATUS_CONFIG[si.status].label}
                </Badge>
              </div>
            </DialogHeader>

            {/* Core fields */}
            <div className="space-y-3">
              {si.description && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Instruction</p>
                  <p className="text-sm whitespace-pre-wrap">{si.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {si.issued_by && (
                  <div>
                    <span className="text-muted-foreground">Issued By: </span>
                    {si.issued_by}
                  </div>
                )}
                {si.issued_date && (
                  <div>
                    <span className="text-muted-foreground">Date Issued: </span>
                    {formatDate(si.issued_date)}
                  </div>
                )}
                {si.drawing_references && (
                  <div>
                    <span className="text-muted-foreground">Drawing Ref: </span>
                    {si.drawing_references}
                  </div>
                )}
                {si.spec_references && (
                  <div>
                    <span className="text-muted-foreground">Spec Ref: </span>
                    {si.spec_references}
                  </div>
                )}
                {si.rfi_id && (
                  <div>
                    <span className="text-muted-foreground">Source RFI: </span>
                    linked
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(si)}>
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
                <Download className="h-3 w-3 mr-1" />
                Download PDF
              </Button>
              {si.status !== 'superseded' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDistributeForm(!showDistributeForm)}
                >
                  <Send className="h-3 w-3 mr-1" />
                  Distribute
                </Button>
              )}
              {allowedTransitions.map((nextStatus) => (
                <Button
                  key={nextStatus}
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusChange(nextStatus as 'issued' | 'superseded')}
                  disabled={updateStatus.isPending}
                >
                  Mark as {SI_STATUS_CONFIG[nextStatus]?.label ?? nextStatus}
                </Button>
              ))}
            </div>

            {/* Distribute form */}
            {showDistributeForm && (
              <>
                <Separator />
                <div className="space-y-3">
                  <p className="text-sm font-medium">Distribute SI</p>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Recipients (comma or newline separated)</Label>
                    <Textarea
                      value={distributeEmails}
                      onChange={(e) => setDistributeEmails(e.target.value)}
                      placeholder="architect@firm.com, engineer@firm.com"
                      rows={3}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Custom Note (optional)</Label>
                    <Input
                      value={distributeNote}
                      onChange={(e) => setDistributeNote(e.target.value)}
                      placeholder="Add a note to this distribution..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleDistribute}
                      disabled={distributeSI.isPending || !distributeEmails.trim()}
                    >
                      {distributeSI.isPending ? 'Sending...' : 'Send'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowDistributeForm(false);
                        setDistributeEmails('');
                        setDistributeNote('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
